import {
  BILLING_FREQUENCY_OPTIONS,
  buildPreviewSubtitle,
  extractBillingTypeSubtitle,
  formatBillingPeriod,
  getBillingTypeRecordId,
  type BillingFrequency,
} from "./billingPeriodUtils";
import type { BillingConfigurationApiRecord } from "./billingListUtils";
import type { InvoicePreviewLineItem } from "./types";

type DropdownOption = {
  value: string;
  label: string;
  subtitle?: string;
};

function parseAmount(value?: string | number): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseRate(value?: string | number): number {
  return parseAmount(value);
}

function resolveBillingTypeValue(
  label: string | undefined,
  options: DropdownOption[]
): string {
  if (!label?.trim()) {
    return "";
  }

  const normalized = label.trim().toLowerCase();
  return (
    options.find(option => option.label.trim().toLowerCase() === normalized)
      ?.value ?? ""
  );
}

function resolveFrequencyValue(frequency?: string): BillingFrequency | "" {
  if (!frequency?.trim()) {
    return "";
  }

  const normalized = frequency.trim().toLowerCase();
  return (
    BILLING_FREQUENCY_OPTIONS.find(
      option => option.label.trim().toLowerCase() === normalized
    )?.value ?? ""
  );
}

function resolveCurrencyValue(
  currencyLabel: string | undefined,
  options: DropdownOption[]
): string {
  if (!currencyLabel?.trim()) {
    return "";
  }

  const normalized = currencyLabel.trim().toLowerCase();
  const match = options.find(option => {
    const label = option.label.trim().toLowerCase();
    const subtitle = option.subtitle?.trim().toLowerCase() ?? "";
    return label === normalized || subtitle === normalized;
  });

  return match?.value ?? "";
}

function inferGstTypeFromApiItem(
  item: NonNullable<BillingConfigurationApiRecord["items"]>[number]
): string | undefined {
  const igst = parseRate(item.igst);
  const cgst = parseRate(item.cgst);
  const sgst = parseRate(item.sgst);

  if (igst > 0) {
    return "outstate";
  }

  if (cgst > 0 || sgst > 0) {
    return "instate";
  }

  return undefined;
}

function resolveBillingTypeLabel(
  billingTypeValue: string,
  fallbackLabel: string,
  options: DropdownOption[]
): string {
  if (billingTypeValue) {
    const fromOption = options.find(
      option => option.value === billingTypeValue
    )?.label;
    if (fromOption?.trim()) {
      return fromOption.trim();
    }
  }

  return fallbackLabel.trim();
}

export function getBillingTypePreviewTitle(
  billingTypeValue: string,
  billingTypeLabel: string,
  billingTypeOptions: DropdownOption[],
  billingTypeRecords: Record<string, unknown>[] = []
): string {
  const fromDropdown = billingTypeOptions
    .find(option => option.value === billingTypeValue)
    ?.subtitle?.trim();
  if (fromDropdown) {
    return fromDropdown;
  }

  const matchedRecord = billingTypeRecords.find(
    record => getBillingTypeRecordId(record) === billingTypeValue
  );
  const fromRecord = extractBillingTypeSubtitle(matchedRecord);
  if (fromRecord) {
    return fromRecord;
  }

  return billingTypeLabel || "Billing Item";
}

export function getBillingTypePreviewSubtitle(
  billingTypeLabel: string,
  frequencyValue: BillingFrequency | string
): string {
  const frequencyLabel =
    BILLING_FREQUENCY_OPTIONS.find(option => option.value === frequencyValue)
      ?.label ?? (typeof frequencyValue === "string" ? frequencyValue : "");

  return buildPreviewSubtitle(billingTypeLabel, frequencyLabel);
}

export function enrichLineItemPreviewDisplay(
  item: InvoicePreviewLineItem,
  billingTypeOptions: DropdownOption[],
  billingTypeRecords: Record<string, unknown>[] = []
): InvoicePreviewLineItem {
  const billingTypeLabel = resolveBillingTypeLabel(
    item.billingType,
    item.title,
    billingTypeOptions
  );
  const title = getBillingTypePreviewTitle(
    item.billingType,
    billingTypeLabel,
    billingTypeOptions,
    billingTypeRecords
  );
  const subtitle = getBillingTypePreviewSubtitle(
    billingTypeLabel,
    item.frequency
  );
  const billingPeriod =
    item.billingStartDate && item.frequency
      ? formatBillingPeriod(
          item.billingStartDate,
          item.frequency as BillingFrequency
        )
      : item.billingPeriod;

  return {
    ...item,
    title,
    subtitle,
    billingPeriod,
  };
}

export function mapBillingRecordToLineItems(
  record: BillingConfigurationApiRecord,
  billingTypeOptions: DropdownOption[],
  currencyOptions: DropdownOption[],
  billingTypeRecords: Record<string, unknown>[] = []
): InvoicePreviewLineItem[] {
  const billingTypeValue = resolveBillingTypeValue(
    record.billing_type,
    billingTypeOptions
  );
  const billingTypeLabel = record.billing_type?.trim() ?? "";
  const frequencyValue = resolveFrequencyValue(record.frequency);
  const billingStartDate = record.binning_start_date ?? "";
  const billingPeriod =
    billingStartDate && frequencyValue
      ? formatBillingPeriod(billingStartDate, frequencyValue)
      : "—";

  return (record.items ?? []).map((item, index) => {
    const amount = parseAmount(item.amount);
    const qty = Number(item.qty) || 1;
    const igst = parseRate(item.igst);
    const cgst = parseRate(item.cgst);
    const sgst = parseRate(item.sgst);
    const gstRate = igst > 0 ? igst : cgst + sgst;
    const itemBillingTypeLabel = item.billing_type?.trim() ?? billingTypeLabel;
    const itemFrequencyValue = resolveFrequencyValue(
      item.frequency ?? record.frequency
    );
    const itemBillingTypeValue =
      resolveBillingTypeValue(itemBillingTypeLabel, billingTypeOptions) ||
      billingTypeValue;
    const itemBillingStartDate =
      item.binning_start_date?.trim() ?? billingStartDate;
    const itemPreviewTitle = getBillingTypePreviewTitle(
      itemBillingTypeValue,
      itemBillingTypeLabel,
      billingTypeOptions,
      billingTypeRecords
    );
    const itemPreviewSubtitle = getBillingTypePreviewSubtitle(
      itemBillingTypeLabel,
      itemFrequencyValue
    );
    const itemBillingPeriod =
      itemBillingStartDate && itemFrequencyValue
        ? formatBillingPeriod(itemBillingStartDate, itemFrequencyValue)
        : billingPeriod;

    return {
      id: `${record.id ?? "config"}-item-${index}`,
      title: itemPreviewTitle,
      subtitle: itemPreviewSubtitle,
      hsnSac: item.hsn_sac ?? "",
      qty,
      rate: qty > 0 ? amount / qty : amount,
      amount,
      billingPeriod: itemBillingPeriod,
      gstApplicable: gstRate > 0,
      gstRate,
      billingType: itemBillingTypeValue,
      frequency: itemFrequencyValue,
      billingStartDate: itemBillingStartDate,
      currency: resolveCurrencyValue(item.currency, currencyOptions),
    };
  });
}

export interface LoadedBillingConfigurationForm {
  projectId?: string;
  invoiceNumber: string;
  clientBillingEmail: string;
  ccEmails: string;
  accountantReviewEmail: string;
  reminderDays: string;
  gracePeriodDays: string;
  nextReminderDate: string;
  notes: string;
  billingType: string;
  frequency: BillingFrequency | "";
  billingStartDate: string;
  configurationGstType?: string;
  lineItems: InvoicePreviewLineItem[];
}

export function mapBillingRecordToForm(
  record: BillingConfigurationApiRecord,
  options: {
    billingTypeOptions: DropdownOption[];
    currencyOptions: DropdownOption[];
    projectOptions: Array<{ value: string; label: string }>;
    billingTypeRecords?: Record<string, unknown>[];
  }
): LoadedBillingConfigurationForm {
  const lineItems = mapBillingRecordToLineItems(
    record,
    options.billingTypeOptions,
    options.currencyOptions,
    options.billingTypeRecords ?? []
  );
  const firstItem = record.items?.[0];
  const firstLineItem = lineItems[0];
  const projectName = record.project?.trim();
  const matchedProject = projectName
    ? options.projectOptions.find(
        option =>
          option.label.trim().toLowerCase() === projectName.toLowerCase() ||
          option.value === projectName
      )
    : undefined;

  return {
    projectId: matchedProject?.value,
    invoiceNumber: record.invoice_number?.trim() ?? "",
    clientBillingEmail: record.client_billing_email?.trim() ?? "",
    ccEmails: (record.cc_emails ?? []).join(", "),
    accountantReviewEmail: record.accountant_review_email?.trim() ?? "",
    reminderDays: String(record.reminder ?? 0),
    gracePeriodDays: String(record.grace_period ?? 0),
    nextReminderDate: record.next_reminder_date?.trim() ?? "",
    notes: record.notes?.trim() ?? "",
    billingType:
      firstLineItem?.billingType ??
      resolveBillingTypeValue(record.billing_type, options.billingTypeOptions),
    frequency: resolveFrequencyValue(
      firstLineItem?.frequency ?? firstItem?.frequency ?? record.frequency
    ),
    billingStartDate:
      firstLineItem?.billingStartDate ??
      firstItem?.binning_start_date ??
      record.binning_start_date ??
      "",
    configurationGstType: firstItem
      ? inferGstTypeFromApiItem(firstItem)
      : undefined,
    lineItems,
  };
}
